import { CalendarDays, CircleCheck, CircleMinus, ShieldCheck, type LucideIcon } from 'lucide-react';
import { toneTextClass, type KpiTone } from '@/components/ui/KpiTile';
import { CalendarEmpty } from '@/components/ui/illustrations/CalendarEmpty';
import { cn } from '@/lib/cn';
import { DashCard, ViewAllButton, drName } from './DashCard';
import type { AppointmentSummary } from './types';

const TILES: Array<{ key: 'scheduled' | 'checked_in' | 'completed' | 'no_show'; label: string; icon: LucideIcon; tone: KpiTone; hint: string }> = [
  { key: 'scheduled', label: 'Scheduled', icon: CalendarDays, tone: 'blue', hint: 'Booked, not yet due' },
  { key: 'checked_in', label: 'Checked-in', icon: ShieldCheck, tone: 'green', hint: 'Arrived; visit in progress' },
  { key: 'completed', label: 'Completed', icon: CircleCheck, tone: 'violet', hint: 'Visit finished' },
  { key: 'no_show', label: 'No-show', icon: CircleMinus, tone: 'red', hint: 'Booked, past the late check-in grace, never arrived' },
];

/**
 * Today's Appointments — docs/design/dashboard region 8. The four statuses
 * are derived server-side (GET /api/appointment-slots/summary) from slot
 * status plus the linked visit, since a slot alone records none of them.
 */
export function AppointmentsCard({ summary, onViewAll }: { summary: AppointmentSummary | null; onViewAll: () => void }) {
  return (
    <DashCard title="Today's Appointments" action={<ViewAllButton onClick={onViewAll} />}>
      <div className="mt-[18px] grid grid-cols-4 gap-3">
        {TILES.map(t => {
          const Icon = t.icon;
          return (
            <div key={t.key} title={t.hint} className="flex h-[103px] flex-col items-center justify-center rounded-md border border-line">
              <Icon className={cn('h-[25px] w-[25px]', toneTextClass(t.tone))} />
              <div className={cn('mt-2.5 text-[10px] leading-tight', toneTextClass(t.tone))}>{t.label}</div>
              <div className="mt-2.5 text-[16px] font-semibold leading-tight text-ink">{summary ? summary[t.key] : '—'}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-[26px] text-[11px] font-bold uppercase tracking-[.02em] text-ink">Upcoming appointments</div>
      {!summary || summary.upcoming.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center text-[10.5px] text-ink-muted">
          <CalendarEmpty className="h-[62px] w-[110px]" />
          No upcoming appointments for today.
        </div>
      ) : (
        <ul className="m-0 mt-1 list-none p-0">
          {summary.upcoming.map(s => (
            <li key={s.id} className="flex gap-2 border-b border-line py-1 text-[11.5px] last:border-b-0">
              <span className="font-mono text-teal">{s.start_time.slice(0, 5)}</span>
              <span className="min-w-0 flex-1 truncate">{s.patient_name ?? '—'} · {drName(s.doctor_name)}</span>
            </li>
          ))}
        </ul>
      )}
    </DashCard>
  );
}
